import { useEffect, useRef, useState } from "react";
import type {
  ManualWorkloadAction,
  ManualWorkloadActionRequest,
  ManualWorkloadKind,
  ManualWorkloadSnapshotResponse,
} from "@k8s-dashboard/contracts";
import { HttpError } from "@/api/client";
import { useManualWorkloadAction, useManualWorkloadSnapshot } from "@/api/queries";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useT } from "@/i18n/I18nProvider";

type PendingAction = {
  action: ManualWorkloadAction;
  snapshot: ManualWorkloadSnapshotResponse;
  replicas?: number;
};

function validSnapshot(
  value: unknown,
  expected: { clusterId: string; kind: ManualWorkloadKind; namespace: string; name: string },
): value is ManualWorkloadSnapshotResponse {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<ManualWorkloadSnapshotResponse>;
  const target = candidate.target;
  const strategy = candidate.strategy;
  return candidate.version === 1 && candidate.clusterId === expected.clusterId &&
    typeof target === "object" && target !== null && target.kind === expected.kind &&
    target.namespace === expected.namespace && target.name === expected.name &&
    typeof target.uid === "string" && typeof target.resourceVersion === "string" &&
    typeof candidate.currentReplicas === "number" && typeof strategy === "object" && strategy !== null &&
    typeof strategy.type === "string" &&
    typeof strategy.paused === "boolean";
}

function sameRenderedIdentity(current: ManualWorkloadSnapshotResponse | undefined, pending: ManualWorkloadSnapshotResponse) {
  return current?.version === pending.version && current.clusterId === pending.clusterId &&
    current.target.kind === pending.target.kind && current.target.namespace === pending.target.namespace &&
    current.target.name === pending.target.name && current.target.uid === pending.target.uid &&
    current.target.resourceVersion === pending.target.resourceVersion;
}

function errorMessage(error: unknown, t: (id: string, vars?: Record<string, string | number>) => string) {
  if (!(error instanceof HttpError)) return t("workload.manage.error.generic");
  switch (error.body.code) {
    case "unknown_outcome":
      return t("workload.manage.error.unknown");
    case "target_changed":
    case "resource_version_conflict":
      return t("workload.manage.error.changed");
    case "hpa_present":
      return t("workload.manage.error.hpaPresent");
    case "hpa_visibility_unknown":
      return t("workload.manage.error.hpaUnknown");
    case "unsupported_strategy":
      return t("workload.manage.error.strategy");
    case "manage_rate_limited":
    case "manage_concurrency":
    case "manage_capacity":
    case "admission_rejected":
      return t("workload.manage.error.busy");
    default:
      return t("workload.manage.error.generic");
  }
}

function restartMessage(kind: ManualWorkloadKind, t: (id: string) => string) {
  switch (kind) {
    case "Deployment":
      return t("workload.manage.confirm.restartDeployment");
    case "StatefulSet":
      return t("workload.manage.confirm.restartStatefulSet");
    case "DaemonSet":
      return t("workload.manage.confirm.restartDaemonSet");
  }
}

/** One shared ADR 0069 action surface used by both Manage entry points. */
export function ManualWorkloadActions({
  clusterId,
  kind,
  namespace,
  name,
  enabled,
}: {
  clusterId: string;
  kind: ManualWorkloadKind;
  namespace: string;
  name: string;
  enabled: boolean;
}) {
  const { t } = useT("workload");
  const snapshotQuery = useManualWorkloadSnapshot(clusterId, kind, namespace, name, enabled);
  const action = useManualWorkloadAction(clusterId, { kind, namespace, name });
  const [replicas, setReplicas] = useState("");
  const [pending, setPending] = useState<PendingAction>();
  const [message, setMessage] = useState<string>();
  const snapshot = validSnapshot(snapshotQuery.data, { clusterId, kind, namespace, name })
    ? snapshotQuery.data
    : undefined;
  const scalable = kind === "Deployment" || kind === "StatefulSet";
  const contextIdentity = `${snapshotQuery.requestIdentity}:${clusterId}:${kind}:${namespace}:${name}:` +
    (snapshot ? `${snapshot.target.uid}:${snapshot.target.resourceVersion}` : "no-snapshot");
  const contextRef = useRef(contextIdentity);
  const contextEpoch = useRef(0);

  useEffect(() => {
    if (contextRef.current === contextIdentity) return;
    contextRef.current = contextIdentity;
    contextEpoch.current += 1;
    setPending(undefined);
    setMessage(undefined);
    action.reset();
  }, [action.reset, contextIdentity]);

  const begin = (nextAction: ManualWorkloadAction) => {
    setMessage(undefined);
    if (!snapshot) {
      setMessage(t("workload.manage.error.snapshot"));
      return;
    }
    if (nextAction === "Scale") {
      const desired = Number(replicas);
      if (!Number.isInteger(desired) || desired < 0 || desired > 1000 || replicas.trim() === "") {
        setMessage(t("workload.manage.error.replicas"));
        return;
      }
      setPending({ action: nextAction, replicas: desired, snapshot: {
        ...snapshot, target: { ...snapshot.target }, strategy: { ...snapshot.strategy },
      } });
      return;
    }
    setPending({ action: nextAction, snapshot: {
      ...snapshot, target: { ...snapshot.target }, strategy: { ...snapshot.strategy },
    } });
  };

  const confirm = async () => {
    if (!pending || !sameRenderedIdentity(snapshot, pending.snapshot)) {
      setPending(undefined);
      setMessage(t("workload.manage.error.changed"));
      return;
    }
    const request: ManualWorkloadActionRequest = {
      version: 1,
      action: pending.action,
      target: pending.snapshot.target,
      ...(pending.action === "Scale" ? { parameters: { replicas: pending.replicas } } : {}),
    };
    const submittedContext = contextIdentity;
    const submittedEpoch = contextEpoch.current;
    try {
      const response = await action.mutateAsync(request);
      if (contextRef.current !== submittedContext || contextEpoch.current !== submittedEpoch) return;
      setMessage(response.outcome === "scaled"
        ? t("workload.manage.success.scaled", { replicas: response.desiredReplicas ?? pending.replicas ?? 0 })
        : t("workload.manage.success.restartRequested"));
      setPending(undefined);
    } catch (error) {
      if (contextRef.current !== submittedContext || contextEpoch.current !== submittedEpoch) return;
      setMessage(errorMessage(error, t));
      setPending(undefined);
    }
  };

  if (!enabled) return null;
  if (snapshotQuery.isLoading) {
    return <div className="state" aria-busy="true"><span className="state__title">{t("workload.manage.loading")}</span></div>;
  }
  if (snapshotQuery.isError || !snapshot) {
    return (
      <div className="state" role="alert">
        <span className="state__title">{t("workload.manage.error.snapshot")}</span>
        <button type="button" className="linkish" onClick={() => void snapshotQuery.refetch()}>{t("workload.manage.refresh")}</button>
      </div>
    );
  }

  const confirmMessage = pending?.action === "Scale"
    ? t("workload.manage.confirm.scale", { current: pending.snapshot.currentReplicas, desired: pending.replicas ?? 0 })
    : restartMessage(kind, t);
  const facts: Array<[string, string | number, string?]> = [
    ["Kind", snapshot.target.kind, "ds-ident"],
    ["Namespace", snapshot.target.namespace, "ds-ident"],
    ["Name", snapshot.target.name, "ds-ident"],
    ["UID", snapshot.target.uid, "ds-ident"],
    ["resourceVersion", snapshot.target.resourceVersion, "ds-ident"],
    [t("workload.manage.currentReplicas"), snapshot.currentReplicas, "num"],
    [t("workload.manage.strategy"), snapshot.strategy.type || "-"],
  ];

  return (
    <section className="manage-manual-actions" aria-label={t("workload.manage.title")}>
      <h3 className="manage-detail__heading">{t("workload.manage.title")}</h3>
      <div className="facts" data-testid="manual-workload-snapshot">
        {facts.map(([label, value, modifier]) => (
          <div className="fact" key={label}>
            <span className="fact__label">{label}</span>
            <span className={`fact__value${modifier ? ` ${modifier}` : ""}`}>{value}</span>
          </div>
        ))}
      </div>
      <div className="row row--wrap" style={{ marginTop: "var(--space-3)" }}>
        {scalable && (
          <label className="row">
            <span>{t("workload.manage.desiredReplicas")}</span>
            <input
              aria-label={t("workload.manage.desiredReplicas")}
              className="ds-input"
              type="number"
              min={0}
              max={1000}
              step={1}
              value={replicas}
              onChange={(event) => setReplicas(event.target.value)}
            />
            <button type="button" className="btn" disabled={action.isPending} onClick={() => begin("Scale")}>
              {t("workload.manage.scale")}
            </button>
          </label>
        )}
        <button type="button" className="btn" disabled={action.isPending} onClick={() => begin("Restart")}>
          {t("workload.manage.restart")}
        </button>
        <button type="button" className="linkish" disabled={snapshotQuery.isFetching || action.isPending} onClick={() => void snapshotQuery.refetch()}>
          {t("workload.manage.refresh")}
        </button>
      </div>
      {message && <div className="state" role="status"><span className="state__title">{message}</span></div>}
      <ConfirmDialog
        open={pending !== undefined}
        title={pending?.action === "Scale" ? t("workload.manage.scale") : t("workload.manage.restart")}
        message={confirmMessage}
        confirmLabel={t("workload.manage.confirm")}
        busy={action.isPending}
        testId="manual-workload-confirm"
        onCancel={() => setPending(undefined)}
        onConfirm={() => void confirm()}
      />
    </section>
  );
}
